import { useCallback, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  fileReducer,
  FileSelection,
  GoogleFontIcon,
  Modal,
  Upload,
} from '@asset-metrix/portal-ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';

import { uploadZipArchive } from '../../api/portalGateway/fileUpload';
import { extractErrorMessage } from '../../utils/errorMessage';

import styles from './zip-upload-modal.module.scss';

export type ZipUploadModalProps = {
  contentId?: string;
  onClose: () => void;
  onUploadSuccess?: () => Promise<unknown>;
  opened: boolean;
  withinPortal?: boolean;
};

export function ZipUploadModal({
  contentId,
  onClose,
  onUploadSuccess,
  opened,
  withinPortal,
}: ZipUploadModalProps) {
  const { t } = useTranslation();
  const [fileState, dispatch] = useReducer(fileReducer, []);

  const uploadFile = useCallback(
    async (file: FileSelection) => {
      try {
        dispatch({
          type: 'updateFileSelectionUploadState',
          payload: { id: file.id, newState: 'isPending' },
        });
        const res = await uploadZipArchive(file.file, contentId);

        if (res.status === 'success') {
          dispatch({
            type: 'updateFileSelectionUploadState',
            payload: { id: file.id, newState: 'isSuccess' },
          });
        } else {
          dispatch({
            type: 'updateFileSelectionUploadState',
            payload: {
              id: file.id,
              newState: 'isError',
              error: extractErrorMessage(res),
            },
          });
        }
      } catch {
        dispatch({
          type: 'updateFileSelectionUploadState',
          payload: { id: file.id, newState: 'isError' },
        });
      } finally {
        await onUploadSuccess?.();
      }
    },
    [contentId, onUploadSuccess]
  );

  const cleanupAndClose = () => {
    dispatch({ type: 'resetFileSelections' });
    onClose();
  };

  const queryClient = useQueryClient();

  const { mutateAsync } = useMutation({ mutationFn: uploadFile }, queryClient);

  const isInitial = fileState.every(
    (selection) => selection.uploadState === 'initial'
  );

  const isPending = fileState.some(
    (selection) => selection.uploadState === 'isPending'
  );

  const showClose =
    !isInitial &&
    !isPending &&
    fileState.some((selection) => selection.uploadState === 'isSuccess');

  return (
    <Modal
      actions={
        <>
          <Button
            variant="secondary"
            onClick={cleanupAndClose}
            className={clsx({
              [styles['hidden']]: showClose,
            })}
          >
            {t('app.upload.cancel')}
          </Button>
          {showClose ? (
            <Button
              icon={<GoogleFontIcon name="check" />}
              onClick={cleanupAndClose}
            >
              {t('app.upload.close')}
            </Button>
          ) : (
            <Button
              icon={isPending && <GoogleFontIcon name="hourglass_top" />}
              onClick={() => {
                if (fileState[0]) {
                  mutateAsync(fileState[0]);
                }
              }}
              disabled={!isInitial || !fileState[0]}
            >
              {t('app.upload.upload')}
            </Button>
          )}
        </>
      }
      modalType="action"
      opened={opened}
      onClose={cleanupAndClose}
      size={768}
      title={t('app.upload.zipUploadModal.headline')}
      withinPortal={withinPortal}
    >
      <Upload
        fileState={fileState}
        dispatch={dispatch}
        accept={['application/zip', 'application/x-zip-compressed']}
        maxFiles={1}
        uploadFile={uploadFile}
        maxSize={5 * 10 ** 7}
      />
    </Modal>
  );
}
